import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
// Api
import { CREATE_TICKET_EVENT_IMAGE_UPLOAD_URLS } from 'api/ticketing/mutations';
// Types
import {
  CreateTicketEventImageUploadUrls,
  CreateTicketEventImageUploadUrlsVariables,
} from 'api/ticketing/types/CreateTicketEventImageUploadUrls';
// Helpers
import { getProperErrorMessage } from 'helpers/errors';

const EXTENSION_BY_TYPE: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpeg',
  'image/webp': 'webp',
};

export const getImageExtension = (file: File): string => {
  const parts = file.name.split('.');

  return parts.length > 1
    ? parts[parts.length - 1].toLowerCase()
    : EXTENSION_BY_TYPE[file.type] || '';
};

export const useUploadTicketEventImage = (): ((
  file: File
) => Promise<string>) => {
  const [createUploadUrls] = useMutation<
    CreateTicketEventImageUploadUrls,
    CreateTicketEventImageUploadUrlsVariables
  >(CREATE_TICKET_EVENT_IMAGE_UPLOAD_URLS);

  const upload = useCallback(
    async (file: File): Promise<string> => {
      const { data } = await createUploadUrls({
        variables: {
          input: {
            files: [{ contentType: file.type, ext: getImageExtension(file) }],
          },
        },
      });
      const presigned = data?.createTicketEventImageUploadUrls[0];

      if (!presigned) {
        throw new Error('Could not prepare the image upload');
      }

      const formData = new FormData();
      Object.entries(JSON.parse(presigned.fields)).forEach(([key, value]) =>
        formData.append(key, value as string)
      );
      formData.append('key', presigned.key);
      formData.append('Content-Type', file.type);
      formData.append('file', file);

      const response = await fetch(presigned.url, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('The image upload failed');
      }

      return presigned.key;
    },
    [createUploadUrls]
  );

  // Whatever step fails, the admin needs to know it was the cover, not the event
  return useCallback(
    (file: File): Promise<string> =>
      upload(file).catch((error) => {
        throw new Error(
          `The cover couldn't be uploaded (${getProperErrorMessage(
            error,
            (error as Error)?.message || 'unknown error'
          )}). Remove it or try another image.`
        );
      }),
    [upload]
  );
};
